import React from 'react'

const IorCostCard = ({
    formData,
    estimateSummary,
    totalItemsValue,
    airfreight,
    insurance,
    cif,
    duty,
    collectionValue,
    hasEstimated,
    // Discount display props
    discountAmount = 0,
    discountType = 'percentage',
    discountValue = 0,
    // Discount input control props (handlers from parent)
    onDiscountTypeChange,
    onDiscountValueChange,
    discountedTotal = null,
}) => {
    const userCurrency = formData.currency || "USD";

    // Only show data if estimation has been run
    if (!hasEstimated) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-8">
                <p className="text-gray-500 text-center">
                    Click "Get Quotation" to see cost breakdown
                </p>
            </div>
        );
    }

    // Get services and taxes from the comprehensive estimate summary
    const serviceData = estimateSummary?.services?.map(service => ({
        name: service.description,
        value: service.value,
    })) || [];

    const dhlTaxes = estimateSummary?.dhlTaxes?.map(tax => ({
        name: tax.name,
        value: tax.value,
    })) || [];

    // Combine — only show items with value > 0
    const data = [
        ...serviceData.filter(item => item.value > 0),
        ...dhlTaxes.filter(item => item.value > 0)
    ];

    // The total already reflects discount (parent passes discountedSummary)
    const totalCost = discountedTotal ?? estimateSummary?.total ?? 0;

    const formatCurrency = (value) => {
        try {
            return new Intl.NumberFormat('en', {
                style: 'currency',
                currency: userCurrency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value);
        } catch (e) {
            console.error("Currency formatting error:", e);
            return `${userCurrency} ${Number(value).toFixed(2)}`;
        }
    };

    const hasDiscount = discountAmount > 0;
    const discountLabel = discountType === 'percentage'
        ? `Discount (${discountValue}%)`
        : `Discount (Fixed)`;

    return (
        <div className="w-full h-full flex flex-col items-start justify-center gap-4 rounded-xl">
            {/* Service rows */}
            <div className="flex flex-grow flex-col w-full gap-4">
                {data.map((item, idx) => (
                    <div key={idx} className="w-full flex items-center justify-between border rounded-xl shadow-md hover:shadow-xl transition-all duration-300 ease-in-out px-4 py-4">
                        <div className="flex items-center gap-2 w-full">
                            <div className={`w-3 h-3 rounded-full custom-blink bg-blue`} />
                            <p>{item.name}</p>
                        </div>
                        <p className="font-semibold flex-shrink-0 ml-2 w-24">{formatCurrency(item.value)}</p>
                    </div>
                ))}

                {/* Discount applied row — shown once a discount value is entered */}
                {hasDiscount && (
                    <div className="w-full flex items-center justify-between border border-orange-200 bg-orange-50 rounded-xl shadow-md px-4 py-4">
                        <div className="flex items-center gap-2 w-full">
                            <div className="w-3 h-3 rounded-full bg-orange-400" />
                            <p className="text-orange-700 font-medium">{discountLabel}</p>
                        </div>
                        <p className="font-semibold flex-shrink-0 ml-2 w-24 text-orange-600">
                            - {formatCurrency(discountAmount)}
                        </p>
                    </div>
                )}
            </div>

            {/* ── Discount Input Panel (inside card, above total) ── */}
            {onDiscountTypeChange && onDiscountValueChange && (
                <div className="w-full rounded-xl border border-dashed border-orange-300 bg-orange-50/50 p-3">
                    <p className="text-xs font-semibold text-orange-700 mb-2">Discount</p>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        {/* Toggle buttons */}
                        <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
                            <button
                                type="button"
                                onClick={() => { onDiscountTypeChange('percentage'); onDiscountValueChange(''); }}
                                className={`px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                                    discountType === 'percentage'
                                        ? 'bg-orange-500 text-white shadow-sm'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                % Percentage
                            </button>
                            <button
                                type="button"
                                onClick={() => { onDiscountTypeChange('value'); onDiscountValueChange(''); }}
                                className={`px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                                    discountType === 'value'
                                        ? 'bg-orange-500 text-white shadow-sm'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {userCurrency} Fixed
                            </button>
                        </div>

                        {/* Input group: prefix box + input field */}
                        <div className="relative flex-1 flex rounded-lg border border-gray-200 overflow-hidden focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-200 transition-all bg-white">
                            {/* Prefix label */}
                            <span className="flex items-center px-2.5 bg-gray-50 border-r border-gray-200 text-gray-500 text-xs font-medium select-none shrink-0">
                                {discountType === 'percentage' ? '%' : userCurrency}
                            </span>
                            {/* Input */}
                            <input
                                type="number"
                                min="0"
                                max={discountType === 'percentage' ? 100 : undefined}
                                step="0.01"
                                value={typeof discountValue === 'number' && discountValue === 0 ? '' : discountValue}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    // Allow clearing the field
                                    if (raw === '' || raw === null) {
                                        onDiscountValueChange('');
                                        return;
                                    }
                                    const num = parseFloat(raw);
                                    // Block non-numeric and negative values
                                    if (isNaN(num) || num < 0) return;
                                    // Clamp percentage at 100
                                    if (discountType === 'percentage' && num > 100) return;
                                    onDiscountValueChange(raw);
                                }}
                                onKeyDown={(e) => {
                                    // Block 'e', 'E', '+', '-' characters
                                    if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                                }}
                                onWheel={(e) => e.target.blur()}
                                placeholder="e.g. 10"
                                className="flex-1 px-2.5 py-1.5 text-xs outline-none bg-transparent"
                            />
                        </div>

                        {/* Live badge */}
                        {hasDiscount && (
                            <span className="shrink-0 text-xs font-semibold text-green-600">
                                - {formatCurrency(discountAmount)}
                            </span>
                        )}
                    </div>

                    <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                        Note: Discount applies exclusively to base service charges. Customs duties, VAT, and statutory taxes are excluded.
                    </p>
                </div>
            )}


            {/* Total footer */}
            <div className={`rounded-lg border-2 border-dashed flex items-center justify-between p-2 text-sm w-full ${hasDiscount ? 'bg-orange-50 border-orange-300' : 'bg-gray-50 text-gray-500'}`}>
                <p className={hasDiscount ? 'text-orange-700 font-medium' : ''}>
                    {formData.selectedService} • Currency: {userCurrency}
                </p>
                <p className={`font-semibold flex-shrink-0 ml-2 w-24 ${hasDiscount ? 'text-orange-700' : 'text-black'}`}>
                    {formatCurrency(totalCost)}
                </p>
            </div>
        </div>
    );
};

export default IorCostCard;